import { map } from "lodash";
import DropDown from "../../shared/DropDown";
import { compressObjectIds } from "../../../helpers/objectIdCompression";

// An appointment's calendars, as far as the viewer may see them
// (Channels::AppointmentsWidgetSerializer#linked_calendars)
export type WidgetCalendar = {
  id: string;
  name: string;
  color?: string;
  group_slug?: string | null;
};

// The appointments module filtered down to that one calendar, the target the
// calendar list of the module links to as well.
function calendarHref(calendar: WidgetCalendar) {
  const query = `?calendarIds=${compressObjectIds([calendar.id])}`;

  return calendar.group_slug
    ? `/groups/${calendar.group_slug}/calendars${query}`
    : `/appointments${query}`;
}

// Like the stream visibilities of the post module (tixxt/core#469): the first
// calendar inline, the rest behind a "+N weitere" dropdown, so a long list
// cannot blow up the card. The list rows show the same, one size smaller
// (tixxt/core#469 follow-up).
export default function CalendarLinks({
  calendars,
  small = false,
}: {
  calendars: WidgetCalendar[];
  small?: boolean;
}) {
  const [first, ...rest] = calendars;

  return (
    <div
      className={`appointment-calendars flex gap-1 items-center ${
        small ? "text-xs" : "text-sm"
      }`}
    >
      {small ? (
        // the row's other lines are led by fixed-width icons (fa-fw, 1.25em),
        // so the dot takes the same lane and the texts line up
        <span className="w-[1.25em] flex justify-center shrink-0">
          <i
            className="w-2 h-2 block rounded-full"
            style={{ backgroundColor: `#${first.color}` }}
          />
        </span>
      ) : (
        <i
          className="w-3 h-3 block rounded-full shrink-0"
          style={{ backgroundColor: `#${first.color}` }}
        />
      )}
      <a href={calendarHref(first)}>{first.name}</a>
      {rest.length > 0 && (
        <DropDown.Wrapper>
          <span>+</span>
          <DropDown.Toggle
            className="more-calendars-link p-0 no-underline hover:underline"
            showIcon={false}
            buttonType="link"
            small
          >
            {I18n.t("js.widgets.appointment.more_calendars", {
              num: rest.length,
            })}
          </DropDown.Toggle>
          {/* the card clips whatever grows past its edge, so the menu opens
              towards the card and stays narrow enough for the smallest tile;
              a name too long for that is cut and shown in full on hover */}
          <DropDown
            alignment="end"
            className="max-w-48 max-h-40 overflow-y-auto"
          >
            {map(rest, (calendar) => (
              <DropDown.Item
                key={calendar.id}
                title={calendar.name}
                text={calendar.name}
                url={calendarHref(calendar)}
                linkStyle="block truncate"
              />
            ))}
          </DropDown>
        </DropDown.Wrapper>
      )}
    </div>
  );
}
